
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Check, X, Zap, Crown, Building, CreditCard } from 'lucide-react';

interface PlanUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: string;
  requiredFeature: string;
  suggestedPlans: string[];
}

const PlanUpgradeModal: React.FC<PlanUpgradeModalProps> = ({
  isOpen,
  onClose,
  currentPlan,
  requiredFeature,
  suggestedPlans
}) => {
  const handleUpgrade = (planType: string) => {
    // Open Google Form for plan upgrade
    window.open("https://forms.gle/yWSKnznK4DGmTxqn9", "_blank");
  };

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$9.99',
      period: '/month',
      icon: Zap,
      features: ['50 images/month', 'All AI models', 'Batch upload (5 images)', 'Email support'],
      color: 'from-green-600 to-emerald-600'
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$29.99',
      period: '/month',
      icon: Crown,
      features: ['200 images/month', 'HD quality', 'Batch upload (10 images)', 'API access', 'Priority support'],
      color: 'from-purple-600 to-pink-600',
      popular: true
    },
    {
      id: 'business',
      name: 'Business',
      price: '$79.99',
      period: '/month',
      icon: Building,
      features: ['500 images/month', 'Team collaboration', 'Batch upload (20 images)', 'Full API access'],
      color: 'from-orange-600 to-red-600'
    }
  ];

  const getFeatureMessage = () => {
    const messages = {
      batch_upload: 'Batch upload allows you to process multiple images at once, saving time and effort.',
      hd_quality: 'HD quality provides superior image resolution and detail for professional use.',
      api_access: 'API access enables integration with your own applications and workflows.',
      team_features: 'Team collaboration features allow multiple users to work together on projects.'
    };
    return messages[requiredFeature as keyof typeof messages] || 'This feature is available in higher tier plans.';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 border-white/20">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white text-center">
            Upgrade Your Plan
          </DialogTitle>
          <p className="text-gray-300 text-center mt-2">
            {getFeatureMessage()}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {plans.filter(plan => suggestedPlans.includes(plan.id)).map((plan) => {
            const Icon = plan.icon;
            const isRecommended = plan.popular;
            
            return (
              <Card 
                key={plan.id}
                className={`relative bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/15 transition-all duration-300 ${
                  isRecommended ? 'ring-2 ring-purple-500 scale-105' : ''
                }`}
              >
                {isRecommended && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                    Recommended
                  </Badge>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-white text-xl">{plan.name}</CardTitle>
                  <div className="text-3xl font-bold text-white">
                    {plan.price}
                    <span className="text-sm text-gray-400">{plan.period}</span>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-gray-300">
                      <Check className="w-4 h-4 text-green-400 mr-3 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                  
                  <Button 
                    onClick={() => handleUpgrade(plan.id)}
                    className={`w-full mt-6 bg-gradient-to-r ${plan.color} hover:opacity-90 text-white`}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Upgrade to {plan.name}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-blue-300 text-sm text-center">
            <strong>Current Plan:</strong> {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} • 
            <strong> Need Help?</strong> Contact our support team for personalized recommendations.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanUpgradeModal;
